import React, { useState, useEffect } from 'react';
import { managerPhrasesAPI } from '../../services/api';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';

const ManagerPhrases = () => {
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingPhrase, setEditingPhrase] = useState('');
  const [editingEffort, setEditingEffort] = useState('');
  const [newPhrase, setNewPhrase] = useState('');
  const [newEffort, setNewEffort] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchPhrases();
  }, []);

  const fetchPhrases = async () => {
    try {
      setLoading(true);
      const response = await managerPhrasesAPI.getAll();
      setPhrases(response.data);
    } catch (err) {
      setError('Erreur lors du chargement des phrases');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newPhrase.trim()) return;
    
    try {
      await managerPhrasesAPI.create({
        phrase: newPhrase.trim(),
        is_active: true,
        order_index: phrases.length,
        effort_percentage: parseFloat(newEffort) || 0
      });
      setNewPhrase('');
      setNewEffort('');
      setShowAddForm(false);
      fetchPhrases();
    } catch (err) {
      setError('Erreur lors de l\'ajout de la phrase');
    }
  };

  const handleEdit = (phrase) => {
    setEditingId(phrase.id);
    setEditingPhrase(phrase.phrase);
    setEditingEffort(phrase.effort_percentage || '0');
  };

  const handleSave = async (id) => {
    if (!editingPhrase.trim()) return;
    
    try {
      await managerPhrasesAPI.update(id, {
        phrase: editingPhrase.trim(),
        is_active: phrases.find(p => p.id === id)?.is_active ?? true,
        order_index: phrases.find(p => p.id === id)?.order_index ?? 0,
        effort_percentage: parseFloat(editingEffort) || 0
      });
      setEditingId(null);
      setEditingPhrase('');
      setEditingEffort('');
      fetchPhrases();
    } catch (err) {
      setError('Erreur lors de la modification de la phrase');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingPhrase('');
    setEditingEffort('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette phrase ?')) {
      return;
    }
    
    try {
      await managerPhrasesAPI.delete(id);
      fetchPhrases();
    } catch (err) {
      setError('Erreur lors de la suppression de la phrase');
    }
  };

  const handleToggleActive = async (phrase) => {
    try {
      await managerPhrasesAPI.update(phrase.id, {
        phrase: phrase.phrase,
        is_active: !phrase.is_active,
        order_index: phrase.order_index,
        effort_percentage: phrase.effort_percentage || 0
      });
      fetchPhrases();
    } catch (err) {
      setError('Erreur lors de la modification du statut');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Phrases de barrage secrétaire</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Ajouter une phrase</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Formulaire d'ajout */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Ajouter une nouvelle phrase</h3>
          <div className="space-y-4">
            <textarea
              value={newPhrase}
              onChange={(e) => setNewPhrase(e.target.value)}
              placeholder="Entrez la nouvelle phrase..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
            />
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Pourcentage d'effort (%)</label>
              <input
                type="number"
                value={newEffort}
                onChange={(e) => setNewEffort(e.target.value)}
                placeholder="0"
                min="0"
                max="100"
                step="0.01"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-24"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200 flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>Ajouter</span>
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setNewPhrase('');
                  setNewEffort('');
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition duration-200 flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Annuler</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des phrases */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Phrases configurées ({phrases.length})
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {phrases.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              Aucune phrase configurée. Ajoutez votre première phrase pour commencer.
            </div>
          ) : (
            phrases.map((phrase) => (
              <div key={phrase.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    {editingId === phrase.id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editingPhrase}
                          onChange={(e) => setEditingPhrase(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows="2"
                        />
                        <div className="flex items-center space-x-4">
                          <label className="text-sm font-medium text-gray-700">Effort (%)</label>
                          <input
                            type="number"
                            value={editingEffort}
                            onChange={(e) => setEditingEffort(e.target.value)}
                            min="0"
                            max="100"
                            step="0.01"
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-24"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className={`text-sm ${phrase.is_active ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                          {phrase.phrase}
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            phrase.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {phrase.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-xs text-gray-400">
                            Ordre: {phrase.order_index}
                          </span>
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Effort: {phrase.effort_percentage || 0}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {editingId === phrase.id ? (
                      <>
                        <button
                          onClick={() => handleSave(phrase.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleToggleActive(phrase)}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition duration-200 ${
                            phrase.is_active
                              ? 'bg-red-100 text-red-800 hover:bg-red-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {phrase.is_active ? 'Désactiver' : 'Activer'}
                        </button>
                        <button
                          onClick={() => handleEdit(phrase)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(phrase.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerPhrases;
